import Tooltip from '@mui/material/Tooltip'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { THEME } from 'src/config/config'
import * as enVariables from 'src/config/config'

import CustomTooltip from '../CustomTooltip/CustomTooltip'
import styles from './smartsearch.module.css'

export interface SmartSearchProps {
  className?: string
  fetchdata?: (inputValue: string, redirect?: boolean) => void
  searchQuestion?: string
  fromPage?: string
}

export const SmartSearchBox: React.FC<SmartSearchProps> = ({
  className = '',
  fetchdata,
  searchQuestion = '',
  fromPage = '',
  ...props
}) => {
  const router = useRouter()

  const [searchText, setSearchText] = useState(searchQuestion || '')
  const [searchFocused, setSearchFocused] = useState(true)

  useEffect(() => {
    if (enVariables.ASK_AI_NEW_TAB && router.asPath.includes('smartsearch')) {
      if (searchText != searchQuestion && searchQuestion != '') {
        setSearchText(searchQuestion)
      }
    }
  }, [])

  const onFocus = () => {
    setSearchFocused(true)
  }

  const onTextChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value ? e.target.value : ''
    setSearchText(text)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handelSearch()
    }
  }

  const handelSearch = () => {
    if (searchText.trim() === '') {
      //alert('Please enter your search query.')
      return false
    }

    if (enVariables.ASK_AI_NEW_TAB == true) {
      const currentUrl = router.asPath
      if (currentUrl.includes('smartsearch')) {
        fetchdata?.(searchText)
        if (fromPage !== 'homepage') {
          setSearchText('')
        }
      } else {
        // router.push({ pathname: '/smartsearch', query: { searchQuery: searchText } }, '/smartsearch')
        // Check if it already exists
        if (localStorage.getItem('searchText')) {
          localStorage.removeItem('searchText')
        }

        // Set new value
        localStorage.setItem('searchText', searchText)

        if (enVariables.SHOW_FREE_ASK_AI && fromPage == 'homepage') {
          fetchdata?.(searchText, true)
        } else {
          // Redirect with clean URL
          router.push({ pathname: '/smartsearch' })
        }
        if (fromPage !== 'homepage') setSearchText('')
      }
    } else {
      fetchdata?.(searchText)
      if (fromPage !== 'homepage') setSearchText('')
    }
  }

  const tooltipTitle = (
    <i>
      Ask questions about solutions, security certification, features, comparisons between products, legal technology
      within a particular region, and more.
      <br />
      <br />
      Example: <br /> "What are some leading solutions in [X category]?" or "Is [product] SOC 2 certified?"
    </i>
  )
  return (
    <div
      className={cn(
        'relative flex items-center',
        className,
        searchFocused ? ' transition: width 5s, height 5s' : '  transition: width 5s, height 5s',
      )}
      onFocus={onFocus}
    >
      <div
        className={cn(
          'z-20 flex-grow rounded-[1.875rem] border-2 bg-white border-solid flex flex-row items-center pl-[1.3125rem] pr-[0.75rem] h-[60px]',
          {
            'shadow-md': searchFocused && enVariables.ASK_AI_NEW_TAB == false,
          },
          { 'text-darkgray2': THEME === 'ilta' },
          { 'text-textdark': THEME === 'legaltech' },
          { 'mb-[5px] border-none': enVariables.ASK_AI_NEW_TAB && router.pathname === '/smartsearch' },
          { 'shadow-xl': enVariables.ASK_AI_NEW_TAB && searchFocused },

          { 'border-2 border-turquoise border-solid': router.pathname != '/smartsearch' },
        )}
        {...props}
      >
        {enVariables.ASK_AI_NEW_TAB && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="#011d58"
            className="mr-2"
          >
            <path
              d="M10 19L7.5 13.5L2 11L7.5 8.5L10 3L12.5 8.5L18 11L12.5 13.5L10 19ZM18 21L16.75 18.25L14 17L16.75 15.75L18 13L19.25 15.75L22 17L19.25 18.25L18 21Z"
              fill="#011d58"
            />
          </svg>
        )}
        <input
          value={searchText}
          autoFocus
          type="text"
          className={cn(
            styles.searchInput,
            'focus:outline-none grow text-[14px] mr-[14px] leading-[42px]',
            { 'placeholder-darkgray2 font-secondary font-normal': THEME === 'ilta' },
            { 'placeholder-textdark font-primary font-medium': THEME === 'legaltech' },
          )}
          placeholder={
            enVariables.ASK_AI_NEW_TAB
              ? router.pathname === '/smartsearch'
                ? 'Ask another question'
                : 'Ask your question'
              : 'Please enter your question here'
          }
          onChange={onTextChanged}
          onKeyUp={handleKeyPress}
        />
        {searchText === '' ? (
          <button title={''} className={'pt-10 pb-10 ps-5 pe-5' + !searchText ? '' : 'text-turquoise'}>
            <Tooltip title="Search all results" placement="right">
              <div className="rounded-full flex flex-none bg-turquoise w-[2.5rem] h-[2.5rem] cursor-pointer items-center justify-center">
                <IonIcon className="mr-0 !w-[20px] !h-[20px]" name="arrow-forward-sharp" />
              </div>
            </Tooltip>
          </button>
        ) : (
          <button
            disabled={!searchText}
            onClick={handelSearch}
            title={!searchText ? 'Please enter your search query' : ''}
            className={'pt-10 pb-10 ps-5 pe-5' + !searchText ? '' : 'text-turquoise'}
          >
            <Tooltip title={!searchText ? 'Please enter your search query' : 'Search all results'} placement="right">
              <div className="rounded-full flex flex-none bg-turquoise w-[2.5rem] h-[2.5rem] cursor-pointer items-center justify-center">
                <IonIcon className="mr-0 !w-[20px] !h-[20px]" name="arrow-forward-sharp" />
              </div>
            </Tooltip>
          </button>
        )}
      </div>
      {fromPage === 'homepage' && (
        <div className="smarttooltip ml-2">
          <CustomTooltip id="search-button-3" title={tooltipTitle} marginLeft={0.5} placement={'right'}>
            <div className="flex items-center cursor-pointer">
              <IonIcon className="!w-[25px] !h-[25px] text-turquoise italic" name="information-circle-outline" />
            </div>
          </CustomTooltip>
          {/* <Tooltip title={tooltipTitle} classes={{ tooltip: classes.customTooltip }} placement="right">
            <div className="flex items-center cursor-pointer">
              <IonIcon className="!w-[25px] !h-[25px] text-turquoise italic" name="information-circle-outline" />
            </div>
          </Tooltip> */}
        </div>
      )}
    </div>
  )
}
